import { useState, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import { checkNewBadges, checkNewBookBadges, checkNewChallengeBadges, checkNewShareBadges, ALL_BADGES } from '../lib/badgeEngine';

export function useBadges(userId) {
  const [earnedSlugs, setEarnedSlugs] = useState([]);
  const [earnedDates, setEarnedDates] = useState({});
  const [newlyEarned, setNewlyEarned] = useState([]);

  const fetchBadges = useCallback(async () => {
    if (!userId) return;
    const { data } = await supabase
      .from('user_badges')
      .select('badge_slug, awarded_at')
      .eq('user_id', userId);
    const dates = {};
    (data || []).forEach(b => { dates[b.badge_slug] = b.awarded_at; });
    setEarnedDates(dates);
    setEarnedSlugs((data || []).map(b => b.badge_slug));
  }, [userId]);

  const evaluateBadges = useCallback(async (logs) => {
    if (!userId) return;
    const newSlugs = checkNewBadges(logs, earnedSlugs);
    if (!newSlugs.length) return;

    const { error } = await supabase
      .from('user_badges')
      .insert(newSlugs.map(slug => ({ user_id: userId, badge_slug: slug })));

    if (!error) {
      setEarnedSlugs(prev => [...prev, ...newSlugs]);
      const newBadgeData = newSlugs.map(slug => ALL_BADGES.find(b => b.slug === slug)).filter(Boolean);
      setNewlyEarned(newBadgeData);
    }
  }, [userId, earnedSlugs]);

  const evaluateBookBadges = useCallback(async (stats) => {
    if (!userId) return;
    const newSlugs = checkNewBookBadges(stats, earnedSlugs);
    if (!newSlugs.length) return;

    const { error } = await supabase
      .from('user_badges')
      .insert(newSlugs.map(slug => ({ user_id: userId, badge_slug: slug })));

    if (!error) {
      setEarnedSlugs(prev => [...prev, ...newSlugs]);
      const newBadgeData = newSlugs.map(slug => ALL_BADGES.find(b => b.slug === slug)).filter(Boolean);
      setNewlyEarned(newBadgeData);
    }
  }, [userId, earnedSlugs]);

  const evaluateChallengeBadges = useCallback(async (desafioId, totalConcluidos) => {
    if (!userId) return;
    const newSlugs = checkNewChallengeBadges(desafioId, totalConcluidos, earnedSlugs);
    if (!newSlugs.length) return;

    const { error } = await supabase
      .from('user_badges')
      .insert(newSlugs.map(slug => ({ user_id: userId, badge_slug: slug })));

    if (!error) {
      setEarnedSlugs(prev => [...prev, ...newSlugs]);
      const newBadgeData = newSlugs.map(slug => ALL_BADGES.find(b => b.slug === slug)).filter(Boolean);
      setNewlyEarned(newBadgeData);
    }
  }, [userId, earnedSlugs]);

  const evaluateShareBadges = useCallback(async () => {
    if (!userId) return;
    try {
      const { count } = await supabase
        .from('reflexoes_anonimas')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', userId);
      const total = count || 0;
      const newSlugs = checkNewShareBadges(total, earnedSlugs);
      if (!newSlugs.length) return;

      const { error } = await supabase
        .from('user_badges')
        .insert(newSlugs.map(slug => ({ user_id: userId, badge_slug: slug })));

      if (!error) {
        setEarnedSlugs(prev => [...prev, ...newSlugs]);
        const newBadgeData = newSlugs.map(slug => ALL_BADGES.find(b => b.slug === slug)).filter(Boolean);
        setNewlyEarned(newBadgeData);
      }
    } catch {
      // user_id column may not exist yet — silently skip
    }
  }, [userId, earnedSlugs]);

  const dismissNewBadges = () => setNewlyEarned([]);

  const earnedBadges = ALL_BADGES.map(b => ({
    ...b,
    earned: earnedSlugs.includes(b.slug),
    earnedAt: earnedDates[b.slug] || null,
  }));

  return { earnedBadges, newlyEarned, fetchBadges, evaluateBadges, evaluateBookBadges, evaluateChallengeBadges, evaluateShareBadges, dismissNewBadges };
}
